import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Badge,
  Button,
  Tooltip,
  Switch,
  Modal,
  Form,
  Input,
  Select,
  message,
  ConfigProvider,
  App as AntApp,
  Alert,
} from 'antd';
import { useAuthStore } from './stores/authStore';
import { useQuery, useQueryClient } from 'react-query';
import api from './services/api';
import Login from './pages/Login';
import {
  DashboardOutlined,
  UserOutlined,
  ShoppingOutlined,
  TruckOutlined,
  MessageOutlined,
  TagOutlined,
  BellOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BarChartOutlined,
  PictureOutlined,
  TeamOutlined,
  DollarOutlined,
  CalculatorOutlined,
  WalletOutlined,
  EnvironmentOutlined,
  MonitorOutlined,
  ShopOutlined,
  CarOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  RollbackOutlined,
  CalendarOutlined,
  MailOutlined,
  MoneyCollectOutlined,
  GiftOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  NotificationOutlined,
  CodeOutlined,
  MergeCellsOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import WalletInformation from './pages/WalletInformation';
import SalesRevenue from './pages/SalesRevenue';
import LiveTracking from './pages/LiveTracking';
import Products from './pages/Products';
import Orders from './pages/Orders';
import Categories from './pages/Categories';
import Brands from './pages/Brands';
import Units from './pages/Units';
import Coupons from './pages/Coupons';
import Banners from './pages/Banners';
import Blog from './pages/Blog';
import Chat from './pages/Chat';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import CommissionManagement from './pages/CommissionManagement';
import Billing from './pages/Billing';
import OrderBilling from './pages/OrderBilling';
import CombinedBilling from './pages/CombinedBilling';
import Inventory from './pages/Inventory';
import PurchaseOrders from './pages/PurchaseOrders';
import Notifications from './pages/Notifications';
import PublicWebContentManager from './pages/PublicWebContentManager';
import Refunds from './pages/Refunds';
import Subscribers from './pages/Subscribers';
import ContactSubmissions from './pages/ContactSubmissions';
import Currencies from './pages/Currencies';
import Offers from './pages/Offers';
import GiftCertificates from './pages/GiftCertificates';
import Affiliate from './pages/Affiliate';
import FiscalYearReports from './pages/FiscalYearReports';
import OfflineSales from './pages/OfflineSales';
import POS from './pages/POS';
import CustomerProfile from './pages/CustomerProfile';
import './App.css';

const { Header, Sider, Content, Footer } = Layout;

// Label with a "Soon" pill for features that aren't live yet.
const soon = (text) => (
  <span className="menu-soon-item">
    {text}
    <span className="menu-soon">Soon</span>
  </span>
);

// Navigation grouped into labeled sections so each area (Sales, Catalog, Users,
// Delivery, Marketing, System) is clearly separated. Routes/keys are unchanged.
const menuItems = [
  {
    type: 'group',
    label: 'Overview',
    children: [
      { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
      { key: '/analytics', icon: <BarChartOutlined />, label: 'Analytics' },
    ],
  },
  {
    type: 'group',
    label: 'Communication',
    children: [
      { key: '/chat', icon: <MessageOutlined />, label: 'Chat' },
      { key: '/notifications', icon: <BellOutlined />, label: 'Notifications' },
      { key: '/contact-submissions', icon: <MailOutlined />, label: 'Contact Submissions' },
    ],
  },
  {
    type: 'group',
    label: 'Sales & Orders',
    children: [
      { key: '/pos', icon: <ShoppingCartOutlined />, label: 'Point of Sale (POS)' },
      { key: '/orders', icon: <ShoppingOutlined />, label: 'Orders' },
      { key: '/offline-sales', icon: <ShopOutlined />, label: 'Offline Sales' },
      { key: '/refunds', icon: <RollbackOutlined />, label: 'Refunds' },
      { key: '/commissions', icon: <CalculatorOutlined />, label: 'Commissions' },
      { key: '/sales-revenue', icon: <DollarOutlined />, label: 'Sales & Revenue' },
      { key: '/wallet-information', icon: <WalletOutlined />, label: 'Wallet' },
      { key: '/fiscal-years', icon: <CalendarOutlined />, label: 'Fiscal Year Reports' },
    ],
  },
  {
    type: 'group',
    label: 'Catalog',
    children: [
      {
        key: '/inventory',
        icon: <AppstoreOutlined />,
        label: 'Inventory',
        children: [
          { key: '/inventory', icon: <AppstoreOutlined />, label: 'Stock Management' },
          { key: '/purchase-orders', icon: <FileTextOutlined />, label: 'Purchase Orders' },
          { key: '/products', icon: <ShoppingOutlined />, label: 'Products' },
          { key: '/billing', icon: <CalculatorOutlined />, label: 'Invoices' },
          { key: '/order-billing', icon: <FileTextOutlined />, label: 'Order Billing' },
          { key: '/combined-billing', icon: <MergeCellsOutlined />, label: 'Combined Bill' },
        ],
      },
      { key: '/categories', icon: <TagOutlined />, label: 'Categories' },
      { key: '/brands', icon: <TeamOutlined />, label: 'Brands' },
      { key: '/units', icon: <CalculatorOutlined />, label: 'Units' },
    ],
  },
  {
    type: 'group',
    label: 'Users',
    children: [
      {
        key: '/users',
        icon: <TeamOutlined />,
        label: 'Users',
        children: [
          { key: '/users/customers', icon: <UserOutlined />, label: 'Customers' },
          { key: '/users/providers', icon: <ShopOutlined />, label: 'Providers' },
          { key: '/users/delivery', icon: <CarOutlined />, label: 'Delivery' },
        ],
      },
    ],
  },
  {
    type: 'group',
    label: 'Delivery',
    children: [
      { key: '/delivery', icon: <TruckOutlined />, label: 'Delivery' },
      { key: '/live-tracking', icon: <EnvironmentOutlined />, label: 'Live Tracking' },
    ],
  },
  {
    type: 'group',
    label: 'Marketing',
    children: [
      { key: '/coupons', icon: <TagOutlined />, label: 'Coupons' },
      { key: '/banners', icon: <PictureOutlined />, label: 'Banners' },
      { key: '/blog', icon: <FileTextOutlined />, label: 'Blog' },
      {
        key: '/marketing',
        icon: <MoneyCollectOutlined />,
        label: 'Promotions',
        children: [
          { key: '/subscribers', icon: <MailOutlined />, label: 'Subscribers' },
          { key: '/offers', icon: <TagOutlined />, label: 'Offers' },
          {
            key: '/gift-certificates',
            icon: <GiftOutlined />,
            label: soon('Gift Certificates'),
            disabled: true,
          },
          {
            key: '/affiliate',
            icon: <ThunderboltOutlined />,
            label: soon('Affiliate'),
            disabled: true,
          },
        ],
      },
    ],
  },
  {
    type: 'group',
    label: 'System',
    children: [
      { key: '/settings', icon: <SettingOutlined />, label: 'Admin Settings' },
      { key: '/developer', icon: <CodeOutlined />, label: 'Developer Mode' },
      { key: '/public-web-content', icon: <FileTextOutlined />, label: 'Public Web Content' },
    ],
  },
];

// Flatten the grouped menu (groups → items → submenu items) into a { path: label }
// map so the header can show the current page name.
const flattenTitles = (items, acc = {}) => {
  items.forEach((item) => {
    if (item.key && typeof item.label === 'string') acc[item.key] = item.label;
    if (item.children) flattenTitles(item.children, acc);
  });
  return acc;
};
const pageTitles = flattenTitles(menuItems);

function App() {
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);
  const [offline, setOffline] = useState(!navigator.onLine);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, validateToken } = useAuthStore();

  const queryClient = useQueryClient();
  const [maintenance, setMaintenance] = useState(false);
  const [maintLoading, setMaintLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifSubmitting, setNotifSubmitting] = useState(false);
  const [notifForm] = Form.useForm();

  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);
    const handleResize = () => {
      if (window.innerWidth < 768) setCollapsed(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !user) {
      validateToken();
    }
  }, [user, validateToken]);

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: 'Profile' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: handleLogout },
  ];

  // Fetch unread notification count for the navbar badge (only when logged in)
  const { data: unreadNotificationCount = 0 } = useQuery(
    'unread-notification-count',
    async () => {
      try {
        const response = await api.get('/admin/admin-notifications/unread-count');
        return response.data?.data?.unread_count || 0;
      } catch (error) {
        return 0;
      }
    },
    {
      enabled: !!user,
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      retry: 1,
    },
  );

  // System settings — used to reflect the maintenance-mode toggle state in the topbar
  const { data: systemSettings } = useQuery(
    'system-settings',
    async () => {
      try {
        const response = await api.get('/admin/settings');
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    { enabled: !!user, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, retry: 1 },
  );

  useEffect(() => {
    setMaintenance(!!systemSettings?.general?.maintenance_mode);
  }, [systemSettings]);

  const handleMaintenanceToggle = (next) => {
    const apply = async () => {
      setMaintLoading(true);
      try {
        await api.put('/admin/settings', { maintenance_mode: next });
        setMaintenance(next);
        message.success(next ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
      } catch (error) {
        message.error('Failed to update maintenance mode');
      } finally {
        setMaintLoading(false);
      }
    };
    if (next) {
      Modal.confirm({
        title: 'Enable maintenance mode?',
        content: 'Customers will see a maintenance page. Admins keep full access.',
        okText: 'Enable',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: apply,
      });
    } else {
      apply();
    }
  };

  const submitNotification = async (values) => {
    setNotifSubmitting(true);
    try {
      const channels = values.channels || [];
      await api.post('/admin/admin-notifications/send', {
        title: values.title,
        message: values.message,
        type: values.type || 'info',
        target_type: values.target_type || 'all',
        send_push: channels.includes('push'),
        send_email: channels.includes('email'),
        send_sms: channels.includes('sms'),
        priority: 'normal',
      });
      message.success('Notification sent');
      setNotifOpen(false);
      notifForm.resetFields();
      queryClient.invalidateQueries('unread-notification-count');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setNotifSubmitting(false);
    }
  };

  if (!user) {
    return <Login />;
  }

  return (
    <AntApp>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#6d28d9',
            colorInfo: '#6d28d9',
            colorBgContainer: '#ffffff',
            borderRadius: 8,
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          },
          components: {
            Menu: {
              itemColor: '#334155',
              itemSelectedColor: '#6d28d9',
              itemSelectedBg: '#f3f0ff',
              itemHoverColor: '#6d28d9',
              itemHoverBg: '#faf9ff',
              itemActiveBg: '#f3f0ff',
              itemBorderRadius: 8,
              itemMarginInline: 8,
              subMenuItemBg: 'transparent',
            },
            Layout: {
              bodyBg: '#f6f7fb',
              headerBg: '#ffffff',
              siderBg: '#ffffff',
            },
          },
        }}
      >
        <Layout style={{ minHeight: '100vh' }}>
          {offline && (
            <Alert
              message="You are offline. Some features may be unavailable."
              type="warning"
              showIcon
              closable
              style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
              onClose={() => setOffline(false)}
            />
          )}
          <Sider
            width={240}
            breakpoint="md"
            collapsedWidth={window.innerWidth < 768 ? 0 : 80}
            trigger={null}
            collapsible
            collapsed={collapsed}
            style={{ background: '#ffffff' }}
          >
            <div className="admin-brand">
              <img
                src="/images/kankali-logo-mark.png"
                alt="Kankali Mart"
                className="admin-brand-mark"
                onError={(e) => {
                  e.target.src =
                    'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%236d28d9%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
                }}
              />
              {!collapsed && (
                <div className="admin-brand-text">
                  <span className="admin-brand-name">Kankali Mart</span>
                  <span className="admin-brand-tag">Admin</span>
                </div>
              )}
            </div>
            <Menu
              theme="light"
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={({ key }) => {
                navigate(key);
                if (window.innerWidth < 768) setCollapsed(true);
              }}
              style={{ borderRight: 0 }}
            />
          </Sider>
          <Layout>
            <Header
              style={{
                padding: '0 16px',
                background: '#fff',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: '18px', width: 48, height: 48 }}
              />
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingRight: '16px',
                }}
              >
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
                  {pageTitles[location.pathname] || 'Dashboard'}
                </div>
                <Space size={4} align="center">
                  <Tooltip
                    title={maintenance ? 'Maintenance mode is ON' : 'Maintenance mode is OFF'}
                  >
                    <span className="topbar-maint">
                      <ToolOutlined style={{ color: maintenance ? '#dc2626' : '#94a3b8' }} />
                      <span className="topbar-maint-label">Maintenance</span>
                      <Switch
                        size="small"
                        checked={maintenance}
                        loading={maintLoading}
                        onChange={handleMaintenanceToggle}
                      />
                    </span>
                  </Tooltip>
                  <span className="topbar-div" />
                  <Tooltip title="Create notification">
                    <Button
                      type="text"
                      shape="circle"
                      icon={<NotificationOutlined />}
                      onClick={() => setNotifOpen(true)}
                    />
                  </Tooltip>
                  <Tooltip title="Chat">
                    <Button
                      type="text"
                      shape="circle"
                      icon={<MessageOutlined />}
                      onClick={() => navigate('/chat')}
                    />
                  </Tooltip>
                  <Tooltip title="System health">
                    <Button
                      type="text"
                      shape="circle"
                      icon={<MonitorOutlined />}
                      onClick={() => navigate('/developer')}
                    />
                  </Tooltip>
                  <Tooltip title="Settings">
                    <Button
                      type="text"
                      shape="circle"
                      icon={<SettingOutlined />}
                      onClick={() => navigate('/settings')}
                    />
                  </Tooltip>
                  <Tooltip title="Notifications">
                    <Badge count={unreadNotificationCount} size="small" offset={[-2, 4]}>
                      <Button
                        type="text"
                        shape="circle"
                        icon={<BellOutlined />}
                        onClick={() => navigate('/notifications')}
                      />
                    </Badge>
                  </Tooltip>
                  <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                    <Space style={{ cursor: 'pointer', marginLeft: 4 }}>
                      <Avatar src={user?.avatar} icon={<UserOutlined />} />
                      <span className="topbar-username">
                        {user?.name || user?.full_name || 'Admin'}
                      </span>
                    </Space>
                  </Dropdown>
                </Space>
              </div>
            </Header>
            <Content
              style={{
                margin: 0,
                padding: window.innerWidth < 768 ? '12px' : '24px',
                background: '#f6f7fb',
                minHeight: 'calc(100vh - 64px)',
              }}
            >
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/users" element={<Users userType="customer" />} />
                <Route path="/users/customers" element={<Users userType="customer" />} />
                <Route path="/users/providers" element={<Users userType="provider" />} />
                <Route path="/users/delivery" element={<Users userType="delivery" />} />
                <Route path="/products" element={<Products />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/purchase-orders" element={<PurchaseOrders />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/offline-sales" element={<OfflineSales />} />
                <Route path="/pos" element={<POS />} />
                <Route path="/customers/:id" element={<CustomerProfile />} />
                <Route path="/refunds" element={<Refunds />} />
                <Route path="/commissions" element={<CommissionManagement />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/brands" element={<Brands />} />
                <Route path="/units" element={<Units />} />
                <Route path="/coupons" element={<Coupons />} />
                <Route path="/banners" element={<Banners />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/delivery" element={<Users userType="delivery" />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/sales-revenue" element={<SalesRevenue />} />
                <Route path="/wallet-information" element={<WalletInformation />} />
                <Route path="/fiscal-years" element={<FiscalYearReports />} />
                <Route path="/live-tracking" element={<LiveTracking />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/order-billing" element={<OrderBilling />} />
                <Route path="/combined-billing" element={<CombinedBilling />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/public-web-content" element={<PublicWebContentManager />} />
                <Route path="/developer" element={<Settings />} />
                <Route path="/subscribers" element={<Subscribers />} />
                <Route path="/contact-submissions" element={<ContactSubmissions />} />
                <Route path="/currencies" element={<Currencies />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/gift-certificates" element={<GiftCertificates />} />
                <Route path="/affiliate" element={<Affiliate />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Content>
            <Footer
              style={{
                textAlign: 'center',
                background: '#fff',
                borderTop: '1px solid #f0f0f0',
                padding: '12px 24px',
                color: '#999',
                fontSize: 13,
              }}
            >
              &copy; {new Date().getFullYear()} Kankali Mart. All rights reserved.
            </Footer>
          </Layout>
        </Layout>

        {/* Quick create-notification modal (accessible from the topbar) */}
        <Modal
          title="Create Notification"
          open={notifOpen}
          onCancel={() => {
            setNotifOpen(false);
            notifForm.resetFields();
          }}
          onOk={() => notifForm.submit()}
          okText="Send"
          confirmLoading={notifSubmitting}
          destroyOnClose
        >
          <Form
            form={notifForm}
            layout="vertical"
            onFinish={submitNotification}
            requiredMark={false}
          >
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: 'Enter a title' }]}
            >
              <Input placeholder="Notification title" />
            </Form.Item>
            <Form.Item
              name="message"
              label="Message"
              rules={[{ required: true, message: 'Enter a message' }]}
            >
              <Input.TextArea rows={3} placeholder="What do you want to tell them?" />
            </Form.Item>
            <Form.Item name="target_type" label="Send to" initialValue="all">
              <Select
                options={[
                  { value: 'all', label: 'All users' },
                  { value: 'users', label: 'Customers' },
                  { value: 'providers', label: 'Providers' },
                  { value: 'delivery', label: 'Delivery personnel' },
                ]}
              />
            </Form.Item>
            <Form.Item name="type" label="Type" initialValue="info">
              <Select
                options={[
                  { value: 'info', label: 'Info' },
                  { value: 'warning', label: 'Warning' },
                  { value: 'success', label: 'Success' },
                  { value: 'error', label: 'Error' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="channels"
              label="Channels"
              initialValue={['push']}
              rules={[{ required: true, message: 'Pick at least one channel' }]}
            >
              <Select
                mode="multiple"
                options={[
                  { value: 'push', label: 'Push' },
                  { value: 'email', label: 'Email' },
                  { value: 'sms', label: 'SMS' },
                ]}
              />
            </Form.Item>
          </Form>
        </Modal>
      </ConfigProvider>
    </AntApp>
  );
}

export default App;
